import React, { useMemo } from "react";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Data } from "@react-google-maps/api";

ChartJS.register(ArcElement, Tooltip, Legend);

export default function ParticipantDistribution({profile, distribution}:any) {
  const formatKey = (key: string) =>
  key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

  const data = useMemo(() => {
      if (!distribution.percent_breakdown) return null;

      const labelsMap: Record<string, string> = {
        patients: "Patients",
        caregivers: "Caregivers",
        hcp: "Oncology HCPs",
      };

      const colors = ["#9ad363", "#679437", "#3d5f1f"];

      const entries = Object.entries(distribution.percent_breakdown);

      return {
        labels: entries.map(
          ([key, value]) => `${labelsMap[key]} ${value}%`
        ),
        datasets: [
          {
            data: entries.map(([_, value]) => value),
            backgroundColor: colors,
            borderWidth: 0,
            cutout: "55%",
          },
        ],
      };
    }, [distribution.percent_breakdown]);


  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,

    animation: {
      duration: 1500,
      easing: "easeOutQuart" as const, 
    },

    plugins: {
      legend: {
        display: false,
      },
    },
  }), []);

  return (
    <div className="flex flex-col h-full items-center">
      <h1 className="relative inline-block text-white text-center font-extrabold 
  max-w-5xl text-2xl lg:text-4xl font-nunito mb-2 
  bg-gradient-to-r from-[#92D050] to-[#8CC54F] 
  px-6 py-2
  before:content-[''] before:absolute before:left-0 before:top-1/2 
  before:-translate-y-1/2 before:-translate-x-full
  before:border-y-[16px] before:border-r-[16px] before:border-y-transparent 
  before:border-r-[#92D050]

  after:content-[''] after:absolute after:right-0 after:top-1/2 
  after:-translate-y-1/2 after:translate-x-full
  after:border-y-[16px] after:border-l-[16px] after:border-y-transparent 
  after:border-l-[#8CC54F]">
        Who We Spoke To: Patients, Caregivers & HCPs
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">

        <div className="md:col-span-2 lg:col-span-3">
          <h4 className="text-black font-semibold mb-2 text-xl max-w-4xl text-center">Participant Profiles</h4>

         <div className="grid grid-cols-1 gap-2 lg:grid-cols-3">
          {Object.entries(profile ).map(([segment, data]:any) => (
            <div
              key={segment}
              className="bg-[#9ADC54] border border-[#FFF7CE] rounded-md p-2"
            >
              <ul>
                <li>
                  <strong className="text-lg">
                    {formatKey(segment)}
                  </strong>

                  <ul className="list-inside pl-1 flex flex-col gap-2 mt-2">
                    {/* Traits */}
                    <li>
                      <strong>Traits</strong>
                      <ul className="list-disc pl-3">
                        {data.traits?.map((trait: string, i: number) => (
                          <li key={i}>{trait}</li>
                        ))}
                      </ul>
                    </li>

                    {/* Behaviors */}
                    <li>
                      <strong>Behaviors</strong>
                      <ul className="list-disc pl-3">
                        {data.behaviors?.map((behavior: string, i: number) => (
                          <li key={i}>{behavior}</li>
                        ))}
                      </ul>
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
          ))}
         </div>

        </div>


      <div className="flex flex-col items-center">
          <h4 className="text-black font-semibold mb-2 text-xl max-w-4xl text-center">Participant Distribution</h4>
        <div className="relative w-[150px] h-[150px] mt-3">          
        {data && <Doughnut data={data} options={options} />}
        <div className="absolute top-[15%] right-[-40%] text-sm font-semibold">
          Patients<br />{distribution.percent_breakdown.patients}
        </div>

        <div className="absolute bottom-[0%] right-[-40%] text-sm font-semibold">
          Caregivers<br />{distribution.percent_breakdown.caregivers}
        </div>

        <div className="absolute top-[40%] left-[-30%] text-sm font-semibold">
           HCP<br />{distribution.percent_breakdown.hcp}
        </div>
      </div>
      </div>

      </div>

      <div className="bg-[#FFF7CE] border-2 border-[#9ADC54] rounded-xl w-full text-center p-1 mt-2">
        Profiles include {distribution.patients_count} patients, {distribution.caregivers_count} caregivers, {distribution.hcp_count} oncology HCPs across varied AI adoption levels.
      </div>

      
    </div>
  );
}
